/**
 * The icon rail (PM efb0e798): one button per section, an icon with a short
 * label under it, Accelimate-style. Settings-type sections are pinned to the
 * bottom. Neutral (PM 5b6978ff): the club's accent color marks only the
 * active section's indicator bar.
 *
 *  - a single-page section is a link straight to its page;
 *  - a multi-page section is a button that shows its pages in the second
 *    column (SectionNav);
 *  - a locked section (module not bought) is visible, padlocked and inert.
 *
 * Staff group labels (People, Commerce, …) separate the clusters; they are
 * visually hidden but kept as the groups' accessible names.
 */
import React from "react";
import { Link } from "react-router-dom";
import { Lock } from "lucide-react";
import Tooltip from "../../common/Tooltip";
import { isSinglePage, RailSection } from "./railModel";

interface NavRailProps {
  sections: RailSection[];
  /** the section holding the current route */
  activeKey: string | null;
  /** the section whose pages are showing (picked, or the active one) */
  shownKey: string | null;
  onPick: (key: string) => void;
  onNavigate: () => void;
  isDarkMode: boolean;
}

const railItemBase =
  "relative flex w-full flex-col items-center gap-1 rounded-lg px-0.5 py-1.5 text-[10px] font-medium leading-tight tracking-tight transition-colors";

const RailItem: React.FC<{
  section: RailSection;
  active: boolean;
  shown: boolean;
  onPick: (key: string) => void;
  onNavigate: () => void;
  isDarkMode: boolean;
}> = ({ section, active, shown, onPick, onNavigate, isDarkMode }) => {
  const selectedCls = isDarkMode
    ? "bg-gray-800 text-white font-semibold"
    : "bg-gray-200/70 text-gray-900 font-semibold";
  const idleCls = isDarkMode ? "hover:bg-gray-800/70" : "hover:bg-gray-200/50";
  const label = section.shortTitle || section.title;
  const face = (
    <>
      {active && (
        <span
          className="absolute left-0 top-2 bottom-2 w-1 rounded-full bg-[var(--color-primary)]"
          aria-hidden="true"
        />
      )}
      <span className="relative">
        {section.icon}
        {section.locked && (
          <Lock className="absolute -right-2 -bottom-1 w-3 h-3" aria-label="Locked" />
        )}
      </span>
      <span className="max-w-full break-normal text-center line-clamp-2">{label}</span>
    </>
  );

  if (section.locked) {
    return (
      <li>
        <Tooltip content={`${section.title} isn't included in your plan. Contact Club Central to enable it.`}>
          <div
            className={`${railItemBase} opacity-50 cursor-not-allowed select-none`}
            aria-disabled="true"
            aria-label={`${section.title} (locked)`}
          >
            {face}
          </div>
        </Tooltip>
      </li>
    );
  }

  const stateCls = shown || active ? selectedCls : idleCls;

  if (isSinglePage(section)) {
    return (
      <li>
        <Link
          to={section.href}
          className={`${railItemBase} ${active ? selectedCls : idleCls}`}
          aria-label={section.title}
          aria-current={active ? "page" : undefined}
          title={section.title}
          onClick={onNavigate}
        >
          {face}
        </Link>
      </li>
    );
  }

  return (
    <li>
      <button
        type="button"
        className={`${railItemBase} ${stateCls}`}
        aria-label={section.title}
        aria-pressed={shown}
        aria-current={active ? "true" : undefined}
        title={section.title}
        onClick={() => onPick(section.key)}
      >
        {face}
      </button>
    </li>
  );
};

/** Consecutive sections sharing a group label, so the rail can divide them. */
function clusters(sections: RailSection[]): Array<{ label: string | null; sections: RailSection[] }> {
  const out: Array<{ label: string | null; sections: RailSection[] }> = [];
  for (const s of sections) {
    const last = out[out.length - 1];
    if (last && last.label === s.groupLabel) last.sections.push(s);
    else out.push({ label: s.groupLabel, sections: [s] });
  }
  return out;
}

const NavRail: React.FC<NavRailProps> = ({ sections, activeKey, shownKey, onPick, onNavigate, isDarkMode }) => {
  const divider = isDarkMode ? "border-gray-700" : "border-gray-200";
  const main = sections.filter((s) => s.placement === "main");
  const bottom = sections.filter((s) => s.placement === "bottom");
  const renderCluster = (c: { label: string | null; sections: RailSection[] }, i: number) => (
    <div
      key={`${c.label ?? "_"}-${i}`}
      role={c.label ? "group" : undefined}
      aria-label={c.label ?? undefined}
      className={i > 0 ? `mt-1 pt-1 border-t ${divider}` : ""}
    >
      {c.label && <span className="sr-only">{c.label}</span>}
      <ul className="space-y-0.5">
        {c.sections.map((s) => (
          <RailItem
            key={s.key}
            section={s}
            active={activeKey === s.key}
            shown={shownKey === s.key}
            onPick={onPick}
            onNavigate={onNavigate}
            isDarkMode={isDarkMode}
          />
        ))}
      </ul>
    </div>
  );

  return (
    <div
      className={`flex h-full w-20 flex-shrink-0 flex-col border-r ${
        isDarkMode ? "bg-gray-900 text-gray-300 border-gray-700" : "bg-gray-50 text-gray-600 border-gray-200"
      }`}
      data-testid="nav-rail"
    >
      <div className="flex-1 min-h-0 overflow-y-auto px-1 py-1.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {clusters(main).map(renderCluster)}
      </div>
      {bottom.length > 0 && (
        <div className={`flex-shrink-0 border-t ${divider} px-1 py-1.5`}>
          <ul className="space-y-0.5">
            {bottom.map((s) => (
              <RailItem
                key={s.key}
                section={s}
                active={activeKey === s.key}
                shown={shownKey === s.key}
                onPick={onPick}
                onNavigate={onNavigate}
                isDarkMode={isDarkMode}
              />
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default NavRail;
